import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Restaurants from '../assets/Restaurants';
import './RestaurantDetail.css';
import { useMetaTags } from '../hooks/useMetaTags';

function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Find the restaurant by index (since we're using array index as ID)
  const restaurant = Restaurants[parseInt(id)];

  useMetaTags({
    title: restaurant ? `${restaurant.name} 2 for 1 Deal | DXB Dates` : '2 for 1 Restaurant Deals in Dubai | DXB Dates',
    description: restaurant ? `${restaurant.name} - ${restaurant.cuisine} in ${restaurant.location.area}. ${restaurant.description} ${restaurant.price.priceForTwo}. Get 2 for 1 dining deals in Dubai.` : 'Discover the best 2 for 1 restaurant deals and dining offers in Dubai. Perfect for date night in the UAE.',
    ogTitle: restaurant ? `${restaurant.name} 2 for 1 Deal` : 'DXB Dates - Best Dining Deals',
    ogDescription: restaurant ? `${restaurant.name} - ${restaurant.cuisine} in ${restaurant.location.area}. Enjoy 2 for 1 dining deals. ${restaurant.description}` : 'Discover the best 2 for 1 restaurant deals and dining offers in Dubai. Perfect for date night.',
    ogImage: restaurant ? restaurant.images[0] : 'https://dxbdates.com/og-image.jpg'
  });

  if (!restaurant) {
    return (
      <div className="restaurant-detail-error">
        <h2>Restaurant not found</h2>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Back to Home
        </button>
      </div>
    );
  }

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <div className="restaurant-detail">
      {/* Hero Section */}
      <section className="restaurant-hero">
        <div className="restaurant-hero-image">
          <img src={restaurant.images[0]} alt={restaurant.name} />
          <div className="restaurant-hero-overlay">
            <button onClick={handleBackClick} className="back-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
              </svg>
              Back to Home
            </button>
            <div className="restaurant-hero-content">
              <h1>{restaurant.name}</h1>
              <p className="restaurant-cuisine">{restaurant.cuisine}</p>
              <div className="restaurant-rating">
                <span className="rating-stars">★★★★★</span>
                <span className="rating-number">{restaurant.rating.$numberDouble}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Info Section */}
      <section className="restaurant-info">
        <div className="restaurant-info-container">
          <div className="restaurant-main-info">
            <div className="restaurant-description">
              <h2>About This Restaurant</h2>
              <p className="long-description">{restaurant.long_description}</p>
              <p className="short-description">{restaurant.description}</p>
            </div>

            <div className="restaurant-details">
              <div className="detail-item-container">
                <div className="detail-item">
                  <h3>Price Range</h3>
                  <p>{restaurant.price.priceRange}</p>
                  <span className="price-for-two">{restaurant.price.priceForTwo} for two</span>
                </div>

                <div className="detail-item">
                  <h3>Location</h3>
                  <p>{restaurant.location.address}</p>
                  <span className="area">{restaurant.location.area}</span>
                </div>

                <div className="detail-item">
                  <h3>Contact</h3>
                  <p>{restaurant.contactInfo.phone}</p>
                  <a href={`mailto:${restaurant.contactInfo.email}`}>{restaurant.contactInfo.email}</a>
                </div>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="restaurant-features">
            <h3>Features</h3>
            <div className="features-list">
              {restaurant.features.map((feature, index) => (
                <span key={index} className="feature-tag">{feature}</span>
              ))}
            </div>
          </div>

          {/* Date Ideas Section */}
          <div className="restaurant-date-ideas">
            <div className="date-ideas-list">
              {restaurant.dateIdeas.map((idea, index) => (
                <div key={index} className="date-idea-card">
                  <h4>{idea.title}</h4>
                  <p>{idea.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="restaurant-actions">
            <a href={restaurant.contactInfo.website} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Visit Website
            </a>
            <a href={`tel:${restaurant.contactInfo.phone}`} className="btn btn-outline">
              Call Restaurant
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default RestaurantDetail;
